import styles from './index.module.scss';
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type {
  UnityCameraData,
  UnityClickDetail,
  RenderStreamingVideoElement,
  UnityCall,
} from '../../../UnityRenderMap';
import {
  clearUnityBusinessCleanupPayloads,
  setUnityBusinessCleanupPayloads,
} from '../../../UnityRenderMap/businessCleanup';
import { UnityOverlay, useSharedUnityMap } from '../../../SharedUnityMapLayer';
import icon_lightmasterLight from '../../imgs/icon-lightmasterLight.png';
import icon_lightmasterLight_disabled from '../../imgs/icon-lightmasterLight-disabled.png';
import icon_solarLight from '../../imgs/icon-solarLight.png';
import icon_solarLight_disabled from '../../imgs/icon-solarLight-disabled.png';
import icon_workshopLighting from '../../imgs/icon-workshopLighting.png';
import icon_workshopLighting_disabled from '../../imgs/icon-workshopLighting-disabled.png';
import { ShareDataWrapperContext } from '../../../..';
import {
  AreaItem,
  EMPTY_AREA_LIST,
  createGgdLightPayload,
  createSunLightPayload,
  createWorkshopLightPayload,
} from './utils';

interface MapViewProps {
  className?: string;
  children?: React.ReactNode;
  initialCameraData?: UnityCameraData[] | null;
  areaList?: AreaItem[];
  onUnityClick?: (detail: UnityClickDetail) => void;
  onMarkerVisibleChange?: (type: 'ggdLight' | 'sunLight' | 'workshopLight', visible: boolean) => void;
  onReady?: (helpers: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => void | Promise<void>;
}

const UnityMap: FC<MapViewProps> = props => {
  const {
    onReady,
    onUnityClick,
    onMarkerVisibleChange,
    children,
    initialCameraData: _initialCameraData,
    areaList = EMPTY_AREA_LIST,
    ...restProps
  } = props;
  const { isStandardSize } = useContext(ShareDataWrapperContext);
  const { unityReady: sharedUnityReady, callUnity, videoElement, registerUnityClickHandler } = useSharedUnityMap();
  const unityReadyRef = useRef<{ callUnity: UnityCall; videoElement: RenderStreamingVideoElement } | null>(null);
  const lastGgdSyncSignatureRef = useRef<string>('');
  const lastSunSyncSignatureRef = useRef<string>('');
  const lastWorkshopSyncSignatureRef = useRef<string>('');
  const [unityReady, setUnityReady] = useState(false);
  const [markerVisible, setMarkerVisible] = useState({
    ggdLight: true,
    sunLight: true,
    workshopLight: true,
  });

  const ggdList = useMemo(() => areaList.filter(area => area.existGgd), [areaList]);
  const sunList = useMemo(() => areaList.filter(area => area.existSunlight), [areaList]);
  const workshopList = useMemo(() => areaList.filter(area => area.existCircuit), [areaList]);

  const handleUnityClick = useCallback((detail: UnityClickDetail) => onUnityClick?.(detail), [onUnityClick]);

  useEffect(() => registerUnityClickHandler(handleUnityClick), [handleUnityClick, registerUnityClickHandler]);

  const syncUnityGgdLights = useCallback(
    async ({ callUnity, videoElement }: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => {
      if (!ggdList.length) {
        return;
      }

      const payload = createGgdLightPayload(ggdList, videoElement, markerVisible.ggdLight);
      const addCount = payload.data.add[0]?.instances.length ?? 0;
      const syncSignature = JSON.stringify(payload.data);
      if (lastGgdSyncSignatureRef.current === syncSignature) {
        return;
      }

      await callUnity(payload);
      lastGgdSyncSignatureRef.current = syncSignature;
      if (markerVisible.ggdLight && addCount > 0) {
        setUnityBusinessCleanupPayloads('lightGgd', [createGgdLightPayload(ggdList, undefined, false)]);
      } else {
        clearUnityBusinessCleanupPayloads('lightGgd');
      }
    },
    [ggdList, markerVisible.ggdLight]
  );

  useEffect(() => {
    const helpers = unityReadyRef.current;
    if (!unityReady || !helpers) return;

    syncUnityGgdLights(helpers);
  }, [syncUnityGgdLights, unityReady]);

  const syncUnitySunLights = useCallback(
    async ({ callUnity, videoElement }: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => {
      if (!sunList.length) {
        return;
      }

      const payload = createSunLightPayload(sunList, videoElement, markerVisible.sunLight);
      const addCount = payload.data.add[0]?.instances.length ?? 0;
      const syncSignature = JSON.stringify(payload.data);
      if (lastSunSyncSignatureRef.current === syncSignature) {
        return;
      }
      await callUnity(payload);
      lastSunSyncSignatureRef.current = syncSignature;
      if (markerVisible.sunLight && addCount > 0) {
        setUnityBusinessCleanupPayloads('lightSun', [createSunLightPayload(sunList, undefined, false)]);
      } else {
        clearUnityBusinessCleanupPayloads('lightSun');
      }
    },
    [markerVisible.sunLight, sunList]
  );

  useEffect(() => {
    const helpers = unityReadyRef.current;
    if (!unityReady || !helpers) return;

    syncUnitySunLights(helpers);
  }, [syncUnitySunLights, unityReady]);

  const syncUnityWorkshopLights = useCallback(
    async ({ callUnity, videoElement }: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => {
      if (!workshopList.length) {
        return;
      }

      const payload = createWorkshopLightPayload(workshopList, videoElement, markerVisible.workshopLight);
      const syncSignature = JSON.stringify(payload.data);
      if (lastWorkshopSyncSignatureRef.current === syncSignature) {
        return;
      }

      await callUnity(payload);
      lastWorkshopSyncSignatureRef.current = syncSignature;
      if (markerVisible.workshopLight && (payload.data.add[0]?.instances.length ?? 0) > 0) {
        setUnityBusinessCleanupPayloads('lightWorkshop', [createWorkshopLightPayload(workshopList, undefined, false)]);
      } else {
        clearUnityBusinessCleanupPayloads('lightWorkshop');
      }
    },
    [markerVisible.workshopLight, workshopList]
  );

  useEffect(() => {
    const helpers = unityReadyRef.current;
    if (!unityReady || !helpers) return;

    syncUnityWorkshopLights(helpers);
  }, [syncUnityWorkshopLights, unityReady]);

  const handleUnityReady = useCallback(
    (helpers: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => {
      unityReadyRef.current = helpers;
      setUnityReady(true);
      onReady?.(helpers);
    },
    [onReady]
  );

  useEffect(() => {
    if (!sharedUnityReady || !callUnity || !videoElement) return;

    handleUnityReady({ callUnity, videoElement });
  }, [callUnity, handleUnityReady, sharedUnityReady, videoElement]);

  useEffect(() => {
    const videoElement = unityReadyRef.current?.videoElement;
    if (!unityReady || !videoElement) return;

    const normalizeUnityBusinessEventDetail = (event: Event) => {
      const eventDetail = (event as CustomEvent).detail;
      const eventData = eventDetail?.data ?? eventDetail;
      const properties = eventData?.properties;
      const detail = {
        ...(eventDetail?.data ? eventDetail : {}),
        data: {
          ...properties,
          ...eventData,
          properties,
        },
      };

      handleUnityClick(detail);
    };

    videoElement.addEventListener('unity-lightLabelClick', normalizeUnityBusinessEventDetail as EventListener);
    videoElement.addEventListener('unity-sectionLabelClick', normalizeUnityBusinessEventDetail as EventListener);
    window.addEventListener('lightLabelClick', normalizeUnityBusinessEventDetail as EventListener);
    window.addEventListener('sectionLabelClick', normalizeUnityBusinessEventDetail as EventListener);
    return () => {
      videoElement.removeEventListener('unity-lightLabelClick', normalizeUnityBusinessEventDetail as EventListener);
      videoElement.removeEventListener('unity-sectionLabelClick', normalizeUnityBusinessEventDetail as EventListener);
      window.removeEventListener('lightLabelClick', normalizeUnityBusinessEventDetail as EventListener);
      window.removeEventListener('sectionLabelClick', normalizeUnityBusinessEventDetail as EventListener);
    };
  }, [handleUnityClick, unityReady]);

  const toggleMarker = (type: 'ggdLight' | 'sunLight' | 'workshopLight') => {
    setMarkerVisible(prev => {
      const visible = !prev[type];
      onMarkerVisibleChange?.(type, visible);
      return { ...prev, [type]: visible };
    });
  };
  const { className, ...overlayProps } = restProps;

  return (
    <UnityOverlay {...overlayProps} className={`${className ?? ''} ${styles.unityOverlay}`}>
      <div className={styles.legendList} style={{ left: isStandardSize ? '20px' : '520px' }}>
        <div
          className={markerVisible.ggdLight ? styles.activeGgdItem : styles.inActiveItem}
          onClick={() => toggleMarker('ggdLight')}
        >
          <img src={markerVisible.ggdLight ? icon_lightmasterLight : icon_lightmasterLight_disabled} alt="" />
          <div>高杆灯</div>
        </div>
        <div
          className={markerVisible.sunLight ? styles.activeSunLightItem : styles.inActiveItem}
          onClick={() => toggleMarker('sunLight')}
        >
          <img src={markerVisible.sunLight ? icon_solarLight : icon_solarLight_disabled} alt="" />
          <div>太阳能路灯</div>
        </div>
        <div
          className={markerVisible.workshopLight ? styles.activeLightItem : styles.inActiveItem}
          onClick={() => toggleMarker('workshopLight')}
        >
          <img src={markerVisible.workshopLight ? icon_workshopLighting : icon_workshopLighting_disabled} alt="" />
          <div>车间照明</div>
        </div>
      </div>
      {children}
    </UnityOverlay>
  );
};

export default UnityMap;
